import clsx from 'clsx';
import React, { AriaAttributes } from 'react';

export type BodyTextProps = {
  children: React.ReactNode;
  /**
   * Accessibility feature that announces the contents of the region when it changes.
   */
  ariaLive?: AriaAttributes['aria-live'];
  /**
   * Transforms BodyText to be bold when true.
   *
   * @defaultValue `false`
   */
  bold?: boolean;
  /**
   * Sets the color of the text.
   *
   * @defaultValue `black`
   */
  color?: 'black' | 'gray' | 'white' | 'red';
  /**
   * id of the rendered html element
   */
  id?: string;
  /**
   * Transforms BodyText to be inlined when true, renders as <span> element if `href` is not provided.
   *
   * @defaultValue `false`
   */
  inline?: boolean;
  /**
   * Transforms BodyText to be smaller, for use as microcopy.
   *
   * @defaultValue `false`
   */
  small?: boolean;
  /**
   * Transforms BodyText casing.
   *
   * @defaultValue "normal-case"
   */
  textTransform?: 'normal-case' | 'uppercase' | 'lowercase' | 'capitalize';
  /**
   * Transforms BodyText to be underlined when true.
   *
   * @defaultValue `false`
   */
  underline?: boolean;
};

const colorClass: { [color in NonNullable<BodyTextProps['color']>]: `text-${color}` } = {
  black: 'text-black',
  gray: 'text-gray',
  white: 'text-white',
  red: 'text-red',
};

export const BodyText = ({
  ariaLive,
  bold = false,
  children,
  color = 'black',
  id,
  inline = false,
  small = false,
  textTransform = 'normal-case',
  underline = false,
}: BodyTextProps): JSX.Element => {
  const htmlElement = inline ? 'span' : 'p';
  return React.createElement(
    htmlElement,
    {
      'aria-live': ariaLive,
      className: clsx(`${colorClass[color]}`, `${textTransform}`, {
        inline: inline,
        underline: underline,
        'text-16': !small,
        'text-12': small,
        'font-bold': bold,
      }),
      id,
    },
    children,
  );
};
